import { forwardRef, useRef, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { useElementLayout } from '../../hooks/useElementLayout';
import soundEffects from '../../services/sounds/soundEffects';

import type { IStyledButtonProps } from './buttonTypes';
import styles from './StyledButton.module.scss';

const getShadowHeight = (height: number) => {
  if (typeof height !== 'number' || height === 0) {
    return '3px';
  }
  const shadowHeight = height * 0.12;
  return shadowHeight > 4 ? '4px' : `${shadowHeight}px`;
};

export const StyledButton = forwardRef<HTMLButtonElement, IStyledButtonProps>(
  (
    {
      className,
      classNameWrapper = '',
      classNameContent,
      btnType = 'main',
      iconLeft,
      iconRight,
      handleOnClick,
      children,
      dataTestId,
      withCrown,
      ...restProps
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLButtonElement | null>(null);

    const { height } = useElementLayout(internalRef);

    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        internalRef.current = node;

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef && 'current' in forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof handleOnClick === 'function') {
        handleOnClick(e);
      }
      soundEffects.playSound('click');
    };

    const buttonClasses = classNames(
      'button',
      styles.button,
      `${styles[`button--${restProps.disabled ? 'gray' : btnType}`]}`,
      className,
    );

    const wrapperClasses = classNames({
      'animation-popup': true,
      'button-wrapper': true,
      [classNameWrapper]: true,
      [styles.withCrown]: withCrown,
    });

    const bottomShadowHeight = getShadowHeight(height);

    const buttonStyle = useMemo(
      () => ({ ...restProps.style, '--bottom-shadow-height': bottomShadowHeight }),
      [bottomShadowHeight, restProps.style],
    );

    return (
      <div className={wrapperClasses} data-testid={dataTestId}>
        <button className={buttonClasses} onClick={handleClick} {...restProps} ref={setRefs} style={buttonStyle}>
          {iconLeft ? <div className="button__icon-side __left">{iconLeft}</div> : null}
          <div className={classNames('button__content', classNameContent)}>{children}</div>
          {iconRight ? <div className="button__icon-side __right">{iconRight}</div> : null}
        </button>
      </div>
    );
  },
);

StyledButton.displayName = 'Button';
